import { useCallback, useEffect, useRef, useState } from 'react';
import { streamAgentChat, AgentContextEntity, ChatTurn } from '@/api/agent/chat';
import { rateAgentTurn, TurnRating } from '@/api/agent/feedback';
import { describeActionFailure, useApproveAgentAction, useRejectAgentAction } from '@/api/agent/action';

/**
 * Chat state for one session.
 *
 * History is client-held and resent each turn — the backend keeps no conversation state, which is
 * what lets the same endpoint serve another channel later. The cost is that a refresh loses the
 * conversation, which is the right trade for an assistant you open, ask, and close.
 */

/** How many turns are sent back. Beyond this the oldest are dropped rather than summarised. */
const HISTORY_LIMIT = 20;

export type ActionStatus = 'pending' | 'executing' | 'done' | 'failed' | 'rejected';

export interface ChatAction {
  id: string;
  toolName: string;
  summary: string;
  args: Record<string, unknown>;
  status: ActionStatus;
  error?: string;
}

export interface ChatMessage extends ChatTurn {
  /** Present when this bubble is a write-confirmation card rather than prose. */
  action?: ChatAction;
  /**
   * Which turn of the conversation produced this answer, 1-based.
   *
   * The stored row is written after the stream ends and without blocking it, so the client never
   * sees a database id — it addresses a turn by conversation and position instead.
   */
  seq?: number;
  rating?: TurnRating | null;
}

/**
 * What the model is told in place of the visual card.
 *
 * The card itself is a UI affordance the model never sees; without a textual stand-in the next
 * turn's history would show an assistant turn that proposed something and then silence, and the
 * model would propose it again.
 */
const ACTION_AS_TEXT: Record<ActionStatus, string> = {
  pending: '[הפעולה הוצגה למשתמש וממתינה לאישור]',
  executing: '[הפעולה הוצגה למשתמש וממתינה לאישור]',
  done: '[המשתמש אישר והפעולה בוצעה בהצלחה]',
  failed: '[הפעולה נכשלה בביצוע]',
  rejected: '[המשתמש דחה את הפעולה]'
};

const toTurn = (message: ChatMessage): ChatTurn | null => {
  if (message.action) {
    return { role: 'assistant', content: ACTION_AS_TEXT[message.action.status] };
  }
  return message.content ? { role: message.role, content: message.content } : null;
};

export const useAgentChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Records the server resolved in earlier turns, echoed back with the next question.
   *
   * A ref rather than state: nothing renders from it, and it must be readable by the in-flight
   * `send` closure without re-creating it. The server keeps no conversation state, so if this is
   * dropped a follow-up loses its subject — "וכולם פעילים?" then answers about every product in
   * the system rather than the store just discussed.
   */
  const contextRef = useRef<AgentContextEntity[]>([]);
  /** Identity of this conversation, so the stored log reads as one sequence. */
  const conversationIdRef = useRef<string>(crypto.randomUUID());
  /** Questions asked so far — the next answer's `seq`. */
  const turnRef = useRef(0);

  const { mutateAsync: approve } = useApproveAgentAction();
  const { mutateAsync: reject } = useRejectAgentAction();

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setActiveTool(null);
  }, []);

  // Abort an in-flight turn if the panel unmounts, so a closed chat stops spending tokens.
  useEffect(() => stop, [stop]);

  const reset = useCallback(() => {
    stop();
    setMessages([]);
    setError(null);
    // A new conversation is about nothing yet, and is a different conversation in the log.
    contextRef.current = [];
    conversationIdRef.current = crypto.randomUUID();
    turnRef.current = 0;
  }, [stop]);

  const appendDelta = (delta: string, seq: number) => {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      // A card closes the current bubble; further text starts a new one.
      if (!last || last.role !== 'assistant' || last.action) {
        return [...prev, { role: 'assistant', content: delta, seq, rating: null }];
      }
      return [...prev.slice(0, -1), { ...last, content: last.content + delta }];
    });
  };

  const setActionStatus = (id: string, status: ActionStatus, actionError?: string) => {
    setMessages(prev =>
      prev.map(message =>
        message.action?.id === id
          ? { ...message, action: { ...message.action, status, error: actionError } }
          : message
      )
    );
  };

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || isStreaming) return;

      setError(null);
      setIsStreaming(true);

      const outgoing: ChatMessage[] = [...messages, { role: 'user', content }];
      setMessages(outgoing);

      const history = outgoing
        .slice(-HISTORY_LIMIT)
        .map(toTurn)
        .filter((turn): turn is ChatTurn => Boolean(turn));

      const seq = turnRef.current + 1;
      turnRef.current = seq;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await streamAgentChat(
          history,
          contextRef.current,
          conversationIdRef.current,
          event => {
            switch (event.type) {
              case 'text':
                appendDelta(event.delta, seq);
                break;
              case 'tool_start':
                setActiveTool(event.label || event.name);
                break;
              case 'tool_end':
                setActiveTool(null);
                break;
              case 'confirm':
                setMessages(prev => [
                  ...prev,
                  {
                    role: 'assistant',
                    content: '',
                    action: {
                      id: event.id,
                      toolName: event.toolName,
                      summary: event.summary,
                      args: event.args,
                      status: 'pending'
                    }
                  }
                ]);
                break;
              case 'context':
                contextRef.current = event.entities;
                break;
              case 'error':
                setError(event.message);
                break;
              case 'done':
                break;
            }
          },
          controller.signal
        );
      } catch (streamError) {
        // An abort is the user closing the panel or pressing stop, not a failure.
        if ((streamError as Error)?.name !== 'AbortError') {
          setError(streamError instanceof Error ? streamError.message : 'שגיאה בשליחת ההודעה');
        }
      } finally {
        abortRef.current = null;
        setIsStreaming(false);
        setActiveTool(null);
      }
    },
    [isStreaming, messages]
  );

  const confirmAction = useCallback(
    async (id: string) => {
      setActionStatus(id, 'executing');
      try {
        const result = await approve(id);
        setActionStatus(id, result.ok ? 'done' : 'failed', result.ok ? undefined : describeActionFailure(result));
      } catch {
        // Only a transport failure reaches here — `approveAgentAction` accepts every HTTP status,
        // so a refused write is a value, not an exception.
        setActionStatus(id, 'failed', 'לא הצלחתי להגיע לשרת. בדוק את החיבור ונסה שוב.');
      }
    },
    [approve]
  );

  const rejectAction = useCallback(
    async (id: string) => {
      setActionStatus(id, 'rejected');
      try {
        await reject(id);
      } catch {
        // The card already reads as rejected and the server prunes it on TTL — nothing to add.
      }
    },
    [reject]
  );

  /**
   * Records 👍/👎 on an answer.
   *
   * Optimistic, and a failure is deliberately quiet: the rating is a signal for review, and an
   * error toast over a thumbs-up would be more disruptive than the lost signal. Clicking the same
   * rating again clears it, so a misclick is undoable.
   */
  const rateTurn = useCallback(
    async (seq: number, rating: TurnRating) => {
      let next: TurnRating | null = rating;
      setMessages(prev =>
        prev.map(message => {
          if (message.seq !== seq || message.action) return message;
          next = message.rating === rating ? null : rating;
          return { ...message, rating: next };
        })
      );

      try {
        await rateAgentTurn(conversationIdRef.current, seq, next);
      } catch {
        // The turn row may not be written yet, or the network blipped. Not worth interrupting for.
      }
    },
    []
  );

  return {
    messages,
    isStreaming,
    activeTool,
    error,
    send,
    stop,
    reset,
    confirmAction,
    rejectAction,
    rateTurn
  };
};
